import classNames from "classnames/bind";
import Image from "common/blocks/Image";
import useModalContext from "common/providers/modal/useModalContext";
import SmartLink from "common/wrapper/SmartLink";
import ShortlistButton from "features/favourites/components/ShortlistButton";
import { ResolvedConfirmModal } from "resolvers/data/confirmModalResolver";
import { ResolvedVehicleCardSettings } from "resolvers/data/vehicleCardSettingsResolver";
import { ResolvedVehicleDisplaySettings } from "resolvers/pages/pdpPageResolver";
import { getFinanceExampleSidePanel } from "services/motorpoint/financeExampleSidePanel";
import type { ResponseVehicle } from "services/motorpoint/shortlist";
import { vehicleImageOverlayService } from "services/vehicle/vehicleImageOverlayService";
import slugify from "slugify";
import formatCurrency from "utils/formatCurrency";
import formatMiles from "utils/formatMiles";
import getComingSoonVehicleSizedImages from "utils/getComingSoonVehicleSizedImages";
import { vehicleMakeFormatter } from "utils/language-helpers";

import FinanceExampleSidePanel from "./FinanceExampleSidePanel";
import FinanceExampleSidePanelError from "./FinanceExampleSidePanel/error";
import FinanceExampleSidePanelFooter from "./FinanceExampleSidePanel/footer";
import SidebarContent from "./SidebarContent";
import styles from "./VehicleAdCard.module.scss";

const cx = classNames.bind(styles);

export interface ResponseVehicleAdCardProps extends ResponseVehicle {
    className?: string;
    isFullWidth?: boolean;
    isSaved?: boolean;
    isConfirmationNeeded?: boolean;
    removeVehicleModal?: ResolvedConfirmModal;
    hideButton?: boolean;
    googlePlaceId?: string;
    isRecentlyViewed?: boolean;
    onClick?: any;
    vehicleCardSettings: ResolvedVehicleCardSettings;
    vehicleDisplaySettings: ResolvedVehicleDisplaySettings;
}

const ResponseVehicleAdCard: React.FC<ResponseVehicleAdCardProps & { prefetch?: boolean }> = ({
    className,
    isFullWidth,
    isSaved,
    isConfirmationNeeded,
    removeVehicleModal,
    prefetch = false,
    hideButton,
    googlePlaceId,
    isRecentlyViewed = false,
    onClick,
    vehicleCardSettings,
    vehicleDisplaySettings,
    ...vehicle
}) => {
    const { useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();
    const { getImageOverlayForResponseVehicle, getCustomOverlayForResponseVehicle } =
        vehicleImageOverlayService();

    const isAdvertisedStock = vehicle.IsOnSale || vehicle.IsComingSoon;

    let vehicleImageryOrdered = vehicle?.FirstImage?.SizedImages?.sort((a, b) =>
        b.Format.localeCompare(a.Format)
    );

    if (vehicle.IsComingSoon) {
        vehicleImageryOrdered = getComingSoonVehicleSizedImages(
            `coming-soon/${slugify(vehicle.Make.toLowerCase())}`
        );
    } else if (vehicleImageryOrdered == null) {
        vehicleImageryOrdered = getComingSoonVehicleSizedImages("image-coming-soon");
    }

    let imageSrc = vehicleImageryOrdered?.[0]?.Uri;

    const model = vehicle.IsCommercial ? vehicle?.Range : vehicle?.Model;

    const formattedMake = vehicleMakeFormatter(vehicle.Make);
    const formattedModel = vehicleMakeFormatter(model);

    const discountedPrice =
        (vehicle.CurrentPrice ?? 0) + (!vehicle.IsCommercial ? vehicle.VatAmount || 0 : 0);
    const previousPrice =
        vehicle.ShowDiscount &&
        vehicle.PreviousPrice &&
        vehicle.PreviousPrice - discountedPrice > vehicleDisplaySettings.discountSavingThreshold
            ? vehicle.PreviousPrice
            : null;

    const basicPrice = vehicle?.CurrentPrice ?? 0;
    const VATAmount = vehicle?.VatAmount ?? 0;

    const price = basicPrice + VATAmount;
    const originalRetailPriceBasic = vehicle?.OriginalRetailPrice?.BasicPrice ?? 0;
    const originalRetailPriceVat = vehicle?.OriginalRetailPrice?.VATAmount ?? 0;
    const originalRetailPrice = vehicle.IsCommercial
        ? originalRetailPriceBasic
        : originalRetailPriceBasic + originalRetailPriceVat;
    const savingsAgainstOriginalRetailPrice = vehicle.IsCommercial
        ? originalRetailPrice - basicPrice
        : originalRetailPrice - price;

    const showListPriceSaving = vehicleDisplaySettings.showListPriceSavingOnVehicleTile;
    const listPriceSavingThreshold = vehicleDisplaySettings.listPriceSavingThreshold;
    const listPriceSavingRegistrationYearCriteria =
        vehicleDisplaySettings.listPriceSavingRegistrationYearCriteria.split(",");
    const listPriceSavingText = vehicleDisplaySettings.listPriceSavingText;

    const onFooterButtonClick = (
        event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | MouseEvent> | undefined
    ) => {
        event?.preventDefault();

        if (!vehicleCardSettings?.footerSidebarButton) return;

        const sidebar = (vehicleCardSettings.footerSidebarButton.sidebars || {})[
            vehicleCardSettings.footerSidebarButton.params.sidebarId
        ];

        openSidebar({
            title: sidebar.title,
            content: {
                component: () => <SidebarContent {...sidebar.content} />,
            },
        });
    };

    const onFinanceExampleButtonClick = async (
        event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | MouseEvent> | undefined
    ) => {
        event?.preventDefault();

        const financeExample = await getFinanceExampleSidePanel({ vehicleId: vehicle.VehicleId });

        if (!financeExample) {
            openSidebar({
                title: "",
                content: {
                    component: () => <FinanceExampleSidePanelError vehicleId={vehicle.VehicleId} />,
                },
            });
        } else {
            openSidebar({
                title: financeExample.Title,
                content: {
                    component: () => <FinanceExampleSidePanel {...financeExample} />,
                    footerComponent: () => (
                        <FinanceExampleSidePanelFooter
                            vehicleAdvertUri={financeExample.VehicleAdvertUri}
                        />
                    ),
                },
            });
        }
    };

    const campaignImageOverlay = getImageOverlayForResponseVehicle(
        vehicle,
        vehicleCardSettings.marketingCampaigns
    );
    const campaignCustomOverlay = getCustomOverlayForResponseVehicle(
        vehicle,
        vehicleCardSettings.marketingCampaigns
    );

    const hasCampaign = !!campaignCustomOverlay || !!campaignImageOverlay;

    return (
        <SmartLink onClick={onClick} href={vehicle.VehicleAdvertUrl || "#"} prefetch={!!prefetch}>
            <a
                className={cx(
                    "container",
                    {
                        isFullWidth,
                        expired: !isAdvertisedStock,
                        discounted: !!previousPrice,
                    },
                    className
                )}
            >
                <div className={cx("imageWrapper")}>
                    {vehicleImageryOrdered != null && vehicleImageryOrdered?.length > 3 ? (
                        <picture className={cx("pictureWrapper")}>
                            {vehicleImageryOrdered.map((i, index) => {
                                let mediaQueryString: string;
                                switch (i.Size) {
                                    case "Medium":
                                        mediaQueryString = "(max-width: 743px)";
                                        break;
                                    case "Small":
                                        mediaQueryString = "(min-width: 744px)";
                                        break;
                                    default:
                                        return;
                                }
                                return (
                                    <source
                                        key={index}
                                        media={mediaQueryString}
                                        srcSet={i.Uri}
                                        type={`image/${i.Format}`}
                                    />
                                );
                            })}
                            <img
                                src={imageSrc}
                                alt={vehicle?.FirstImage?.Description ?? "Coming Soon"}
                                className={cx("image")}
                                loading="eager"
                            />
                        </picture>
                    ) : (
                        <Image
                            src={imageSrc}
                            alt={vehicle?.FirstImage?.Description ?? "Coming Soon"}
                            layout="fill"
                            objectFit="cover"
                            className={cx("image")}
                            loading="eager"
                        />
                    )}
                    {isAdvertisedStock && !hideButton && (
                        <ShortlistButton
                            vehicleId={vehicle.StockBookNumber}
                            isSaved={isSaved}
                            isConfirmationNeeded={isConfirmationNeeded}
                            removeVehicleModal={removeVehicleModal}
                            tagManagerEventData={{ vehicle, googlePlaceId }}
                        />
                    )}
                </div>
                <div className={cx("content")}>
                    {showListPriceSaving &&
                        listPriceSavingRegistrationYearCriteria.includes(
                            vehicle.RegPlateAgeIdentifier ?? ""
                        ) &&
                        savingsAgainstOriginalRetailPrice >= listPriceSavingThreshold && (
                            <div className={cx("listPriceSavingBanner")}>
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            `Save ` +
                                            formatCurrency(savingsAgainstOriginalRetailPrice, 0) +
                                            ` `,
                                    }}
                                />
                                {listPriceSavingText}
                            </div>
                        )}
                    <div className={cx("head", { isRecentlyViewed })}>
                        <h3 className={cx("title")}>
                            {formattedMake} {formattedModel}
                        </h3>
                        <p className={cx("model")}>{vehicle.Trim}</p>
                    </div>
                    {isAdvertisedStock && (
                        <>
                            <div className={cx("techWrapper")}>
                                {
                                    <p className={cx("mileage")}>{`${vehicle.RegYear ?? ""} (${
                                        vehicle.RegPlateAgeIdentifier
                                    }) reg | ${formatMiles(vehicle.Mileage)}`}</p>
                                }
                                <p
                                    className={cx("fuel")}
                                >{`${vehicle.FuelType} | ${vehicle.WarrantyRemainingMonths} months warranty`}</p>
                            </div>
                            <div className={cx("priceWrapper")}>
                                <div className={cx("buy")}>
                                    {previousPrice && (
                                        <span className={cx("old")}>
                                            Was{" "}
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: formatCurrency(previousPrice, 0),
                                                }}
                                            />
                                        </span>
                                    )}
                                    <span className={cx("current")}>
                                        <span
                                            className={cx("current", "semiBold", {
                                                reducedPrice: !!previousPrice,
                                            })}
                                            dangerouslySetInnerHTML={{
                                                __html: formatCurrency(discountedPrice ?? 0, 0),
                                            }}
                                        />
                                        {vehicle.ShowVatText && (
                                            <span className={cx("withVat")}> + VAT</span>
                                        )}
                                    </span>
                                </div>
                                {!!vehicle.LowestFinanceOption && (
                                    <div className={cx("monthly")}>
                                        <button
                                            type="button"
                                            className={cx("financeExampleButton")}
                                            onClick={onFinanceExampleButtonClick}
                                        >
                                            Finance example
                                        </button>
                                        <span
                                            className={cx("boldPrice")}
                                            dangerouslySetInnerHTML={{
                                                __html: formatCurrency(
                                                    vehicle.LowestFinanceOption,
                                                    0
                                                ),
                                            }}
                                        />
                                        <span className={cx("postfix")}> per month</span>
                                    </div>
                                )}
                            </div>
                            {vehicleCardSettings?.footerSidebarButton && (
                                <div className={cx("footer")}>
                                    <button
                                        type="button"
                                        className={cx("button")}
                                        onClick={onFooterButtonClick}
                                    >
                                        {vehicleCardSettings?.footerSidebarButton.label}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
                {!!previousPrice && !!discountedPrice && isAdvertisedStock && (
                    <div className={cx("reducedPriceBadge")}>
                        <span
                            dangerouslySetInnerHTML={{
                                __html: formatCurrency(previousPrice - discountedPrice, 0),
                            }}
                        />{" "}
                        off
                    </div>
                )}
                {hasCampaign &&
                    (!!campaignImageOverlay ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            className={cx("campaignImageOverlay")}
                            src={campaignImageOverlay}
                            alt="Promotional campaign image"
                        />
                    ) : (
                        <span
                            className={cx("campaignCustomOverlay")}
                            style={{
                                color: campaignCustomOverlay?.colour,
                                backgroundColor: campaignCustomOverlay?.backgroundColour,
                            }}
                        >
                            {campaignCustomOverlay?.copy}
                        </span>
                    ))}
            </a>
        </SmartLink>
    );
};

export default ResponseVehicleAdCard;
